const DIALOG_WIDTH = 322;
const DIALOG_HEIGHT = 380;
const VIEWPORT_MARGIN = 8;

/**
 * Calculate the position for a dialog using position:fixed,
 * based on the bounding rect of the trigger element.
 *
 * Placement priority: preferredPlacement → opposite vertical side → left →
 * right (side fallback only when allowSideFallback is true and neither
 * vertical side fits).
 * Within each axis the dialog is aligned to the opposite edge of the trigger
 * and clamped to stay inside the viewport.
 *
 * @param {HTMLElement} triggerEl              - The element that triggered the dialog
 * @param {object}      [options]
 * @param {number}      [options.width=322]    - Dialog width in px
 * @param {number}      [options.height=400]   - Dialog height in px
 * @param {number}      [options.gap=8]        - Gap between trigger and dialog in px
 * @param {boolean}     [options.allowSideFallback=false]
 *   When true, uses left/right placement if neither vertical side has enough room.
 * @param {'top'|'bottom'} [options.preferredPlacement='bottom']
 *   Which vertical side to try first. Falls back to the other side if it
 *   doesn't fit.
 * @param {'left'|'right'} [options.align='right']
 *   For top/bottom placements, which edge of the trigger the dialog aligns to:
 *   'right' aligns the dialog's right edge to the trigger's right edge,
 *   'left' aligns the dialog's left edge to the trigger's left edge.
 * @returns {{ top: number, left: number, placement: 'top'|'bottom'|'left'|'right' }}
 */
export function calcFixedDialogPosition(triggerEl, options = {}) {
  const {
    width = DIALOG_WIDTH,
    height = DIALOG_HEIGHT,
    gap = VIEWPORT_MARGIN,
    allowSideFallback = false,
    preferredPlacement = 'bottom',
    align = 'right',
  } = options;

  const rect = triggerEl.getBoundingClientRect();
  const vw = window.innerWidth;
  const vh = window.innerHeight;

  const spaceAbove = rect.top;
  const spaceBelow = vh - rect.bottom;
  const spaceLeft = rect.left;
  const spaceRight = vw - rect.right;

  let top;
  let left;
  let placement;

  const fitsAbove = spaceAbove >= height + gap;
  const fitsBelow = spaceBelow >= height + gap;
  const alignedLeft = align === 'left' ? rect.left : rect.right - width;
  const fits = { top: fitsAbove, bottom: fitsBelow };
  const fallbackPlacement = preferredPlacement === 'top' ? 'bottom' : 'top';

  const placeVertical = side => {
    placement = side;
    top = side === 'top' ? rect.top - height - gap : rect.bottom + gap;
    left = alignedLeft;
  };

  if (fits[preferredPlacement]) {
    placeVertical(preferredPlacement);
  } else if (fits[fallbackPlacement]) {
    placeVertical(fallbackPlacement);
  } else if (allowSideFallback && spaceRight >= width + gap) {
    // --- Right side ---
    placement = 'right';
    left = rect.right + gap;
    top = rect.top + (rect.height - height) / 2;
  } else if (allowSideFallback && spaceLeft >= width + gap) {
    // --- Left side ---
    placement = 'left';
    left = rect.left - width - gap;
    top = rect.top + (rect.height - height) / 2;
  } else {
    // Nothing fits cleanly — pick the roomiest vertical side and clamp
    placeVertical(spaceAbove >= spaceBelow ? 'top' : 'bottom');
  }

  // Clamp both axes to stay inside the viewport
  left = Math.max(
    VIEWPORT_MARGIN,
    Math.min(left, vw - width - VIEWPORT_MARGIN)
  );
  top = Math.max(VIEWPORT_MARGIN, Math.min(top, vh - height - VIEWPORT_MARGIN));

  return { top, left, placement };
}

/**
 * Convert calcFixedDialogPosition result into a CSS inline style object.
 *
 * @param {HTMLElement} triggerEl
 * @param {object}      [options]
 * @returns {{ position: string, top: string, left: string }}
 */
export function getFixedDialogStyle(triggerEl, options = {}) {
  const { top, left } = calcFixedDialogPosition(triggerEl, options);
  return {
    position: 'fixed',
    top: `${Math.round(top)}px`,
    left: `${Math.round(left)}px`,
  };
}
